// Pure paywall helpers (2026-10-27). No SDK, no React: unit tested in
// tests/unit/paywall.test.mjs. Copy here carries no hyphens or dashes.

export const TERMS_URL = 'https://www.apple.com/legal/internet-services/itunes/dev/stdeula/';
export const PRIVACY_URL = 'https://www.scorify.to/privacy.html';

export const DISCLOSURE = 'Payment is charged to your Apple ID. Your subscription renews automatically unless you cancel at least 24 hours before the end of the current period. Manage or cancel anytime in Settings.';

export const VALUE_LINES = [
  'Kai coaches you from your real numbers, every day.',
  'Live scores for money, health, habits and mind.',
  'One place that tells you what to do next.',
];

/** Show the paywall only when the server says it is on for this user and they are not entitled. */
export function shouldShowPaywall(sub) {
  if (!sub) return false;
  return sub.paywallEnabled === true && sub.entitled !== true;
}

/** "Save N%" for the annual package against twelve monthly payments. null when it cannot be computed or is not a saving. */
export function savingsPercent(annualPrice, monthlyPrice) {
  const a = Number(annualPrice), m = Number(monthlyPrice);
  if (!Number.isFinite(a) || !Number.isFinite(m) || a <= 0 || m <= 0) return null;
  const yearly = m * 12;
  if (a >= yearly) return null;
  const pct = Math.round((1 - a / yearly) * 100);
  return pct > 0 ? pct : null;
}

const PERIOD_WORD = { annual: 'year', monthly: 'month' };

/** The line under a plan: "7 days free, then $59.99 per year" or "$59.99 per year". Price strings come from StoreKit, never built here. */
export function priceCopy({ period, priceString, eligible }) {
  const word = PERIOD_WORD[period] || period;
  const price = String(priceString || '').trim();
  if (!price) return '';
  return eligible ? `7 days free, then ${price} per ${word}` : `${price} per ${word}`;
}

export function ctaLabel(eligible) {
  return eligible ? 'Start free trial' : 'Subscribe';
}

/** Pick the annual and monthly packages out of offerings.current. */
export function pickPackages(offering) {
  const packages = offering?.availablePackages || [];
  const byType = (type, word) =>
    packages.find((p) => p?.packageType === type)
    || packages.find((p) => String(p?.identifier || '').toLowerCase().includes(word))
    || packages.find((p) => String(p?.product?.identifier || '').toLowerCase().includes(word))
    || null;
  return { annual: byType('ANNUAL', 'annual'), monthly: byType('MONTHLY', 'month') };
}

/** Profile row label for the subscription reason. */
export function subscriptionStatusLabel(reason) {
  switch (reason) {
    case 'beta': return 'Beta access';
    case 'trial': return 'Trial';
    case 'active': return 'Active';
    case 'grace': return 'Grace';
    case 'paywall_off': return 'Included';
    default: return 'Not active';
  }
}

export const PURCHASE_RECEIVED_NOTICE = 'Purchase received. Tap Restore if access does not unlock.';
export const NO_SUBSCRIPTION_NOTICE = 'No active subscription found.';
export const PRICES_FAILED_NOTICE = 'Could not load prices';

/** True when an API response is the server's paywall answer. */
export function isSubscriptionRequired(status, body) {
  return status === 402 || body?.error === 'subscription_required';
}

// ── SOFT PAYWALL / PREVIEW MODE (2026-10-27) ─────────────────────────────────
// "Not now" on the paywall puts the session in preview mode: every tab is
// viewable, a banner under the header reopens the paywall, and the actions
// that write or spend go through requirePro. Cold launch resets it (React
// state, nothing persisted). Copy carries no hyphens or dashes.
export const PREVIEW_BANNER_COPY = 'Preview mode. Start your free trial to use Scorify.';
export const NOT_NOW_LABEL = 'Not now';

/**
 * What the paywall layer renders. ready: the gate is on and the app is past
 * onboarding (profile loaded, onboarding done, no onboarding sheet open).
 * requested: a 402 or a requirePro call asked for it. preview: the user
 * tapped Not now this session. settled: the placement rule is satisfied
 * (returning user, or a new user whose first scores have been on screen).
 */
export function paywallPresentation({ ready, requested, preview, settled } = {}) {
  if (!ready) return 'none';
  if (requested) return 'full';
  if (preview) return 'banner';
  return settled ? 'full' : 'none';
}

/**
 * requirePro(action, { silent }): true when the user may proceed. Gated:
 * opens the paywall (unless silent, for background calls that are nobody's
 * tap) and returns false. The action name is for the opener only.
 */
export function makeRequirePro({ isGated, openPaywall } = {}) {
  return function requirePro(action, { silent = false } = {}) {
    if (typeof isGated === 'function' && isGated()) {
      if (!silent && typeof openPaywall === 'function') openPaywall(action);
      return false;
    }
    return true;
  };
}

// ── DELETE ACCOUNT: cancel the Apple subscription first (2026-10-27) ─────────
// Deleting the account does not cancel an Apple subscription. Native only
// (the web flow has no StoreKit to send the user to); skipped for beta
// access and whenever the server does not say willRenew. Copy carries no
// hyphens or dashes.
export const CANCEL_FIRST_TITLE = 'Cancel your subscription first';
export const CANCEL_FIRST_BODY = 'Your Scorify Pro subscription is billed by Apple. Deleting your account does not cancel it, and Apple will keep charging you until you cancel.';
export const CANCEL_FIRST_PRIMARY = 'Cancel subscription';
export const CANCEL_FIRST_CONTINUE = "I've canceled, continue";
export const CANCEL_FIRST_SKIP = 'Continue without canceling';
export const SUBSCRIPTION_STILL_ACTIVE_LINE = 'Your Apple subscription is still active. Cancel it in Settings > your name > Subscriptions.';

/** Show the cancel-first step before the delete confirmation? `subscription` is the latest GET /api/subscription body. */
export function needsCancelSubscriptionStep({ native, subscription } = {}) {
  if (native !== true) return false;
  if (!subscription || subscription.willRenew !== true) return false;
  if (subscription.reason === 'beta') return false;
  return true;
}
